import { Country } from '@prisma/client';
import { FeatureLaunch } from './featureLaunchEnum';
import {
  ComplianceLaunchStages,
  CurrentLaunchStage,
  EarlyComplianceReleaseCompanyIds,
  EarlyProdReleaseCompanyIds,
  LaunchStages,
} from './internal/launch-stages';
type Env = 'development' | 'staging' | 'demo' | 'production';

const currentEnv = process.env.NEXT_PUBLIC_CLIENT_ENV as Env;

// Returns which launch stages are allowed in the current env:
// - development: DEV, STAGING, DEMO, PROD
// - staging: STAGING, DEMO, PROD
// - demo: DEMO, PROD
// - production: PROD
// If env is invalid, logs an error and returns null.
// This encodes the pipeline rule: DEV => STAGING => DEMO => PROD.
function getAllowedStagesForEnv(
  env: Env
): ReadonlyArray<CurrentLaunchStage> | null {
  switch (env) {
    case 'development':
      return [
        CurrentLaunchStage.DEV,
        CurrentLaunchStage.STAGING,
        CurrentLaunchStage.DEMO,
        CurrentLaunchStage.PROD,
      ];
    case 'staging':
      return [
        CurrentLaunchStage.STAGING,
        CurrentLaunchStage.DEMO,
        CurrentLaunchStage.PROD,
      ];
    case 'demo':
      return [CurrentLaunchStage.DEMO, CurrentLaunchStage.PROD];
    case 'production':
      return [CurrentLaunchStage.PROD];
    default:
      console.error(
        `NEXT_PUBLIC_CLIENT_ENV not set correctly. Value is ${currentEnv}`
      );
      return null;
  }
}

// Returns true when the feature's launch stage is allowed in the current env.
function isLaunchStageVisible(stage?: CurrentLaunchStage): boolean {
  if (!stage) return false;

  const visibleStages = getAllowedStagesForEnv(currentEnv);

  if (!visibleStages) return false;

  return visibleStages.includes(stage);
}

export function isFeatureLaunched(
  feature: FeatureLaunch,
  companyId?: number
): boolean {
  if (feature === FeatureLaunch.THREADS_AND_COMMENTS) {
    return true;
  }

  if (
    currentEnv === 'production' &&
    companyId &&
    EarlyProdReleaseCompanyIds[feature]?.includes(companyId)
  ) {
    return true;
  }

  return isLaunchStageVisible(LaunchStages[feature]);
}

/**
 * Strict-typed gate for the Documents V2 drive view rollout.
 *
 * Unlike `isFeatureLaunched`, this requires `companyId` so callers cannot
 * accidentally evaluate the flag without a viewed-company context (which
 * would silently bypass the `EarlyProdReleaseCompanyIds` allow-list in
 * production). Mirrors the backend's
 * `FeatureFlagService.isDocumentsV2Enabled(companyId)`.
 */
export function isDocumentsV2Launched(companyId: number): boolean {
  return isFeatureLaunched(FeatureLaunch.DocumentsV2DriveView, companyId);
}

export function isComplianceLaunched(
  country: Country,
  companyId?: number
): boolean {
  if (
    currentEnv === 'production' &&
    companyId &&
    EarlyComplianceReleaseCompanyIds[country]?.includes(companyId)
  ) {
    return true;
  }

  return isLaunchStageVisible(ComplianceLaunchStages[country]);
}
